import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { productUpdates } from './content/updates';
import globalStyles from './styles/global.css?inline';

/* Keep the application shell and its styles in the same versioned asset. Pages
 * deployments can briefly expose a freshly rendered HTML shell before a new
 * standalone stylesheet has reached every cache. Inlining the processed CSS in
 * the entry module makes that mismatch impossible: if React can render, its
 * matching styles are already present. */
const appStyle = document.createElement('style');
appStyle.dataset.piixelAppStyles = '';
appStyle.textContent = globalStyles;
document.head.appendChild(appStyle);
document.documentElement.dataset.appStyled = 'true';

/* Stale-deploy vaccine: a tab that was open across a deploy holds an old
 * index.html whose lazy chunks (hashed filenames) may no longer exist — the
 * dynamic import fails and the route renders BLANK. Vite surfaces exactly
 * that as `vite:preloadError`; reload once to pick up the fresh build.
 * sessionStorage guards against a reload loop if the network itself is bad. */
window.addEventListener('vite:preloadError', (e) => {
  try {
    const KEY = 'helix_chunk_reload_at';
    const last = Number(sessionStorage.getItem(KEY) ?? '0');
    if (Date.now() - last < 30_000) return; // reloaded recently and STILL failing → real outage, let it surface
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch { /* private mode: reload without the guard */ }
  e.preventDefault();
  window.location.reload();
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root missing');

const app = (
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>
);

/* §9.6 — these routes ship prerendered HTML (scripts/prerender.mjs): hydrate
 * instead of re-rendering so the static markup carries straight through.
 * Everything else (authed app routes) reaches us via the Pages SPA fallback,
 * which serves the prerendered '/' shell — clear it before mounting so stale
 * landing markup never flashes under the dashboard/builder. */
const PRERENDERED = new Set(['/', '/login', '/signup', '/terms-of-service', '/privacy-policy', '/refund-policy']);
const PRERENDERED_UPDATES = new Set(['/updates', ...productUpdates.map((update) => `/updates/${update.slug}`)]);
const path = window.location.pathname.replace(/\/+$/, '') || '/';
const isPrerendered = PRERENDERED.has(path) || PRERENDERED_UPDATES.has(path);

if (rootEl.hasChildNodes() && isPrerendered) {
  hydrateRoot(rootEl, app);
} else {
  rootEl.replaceChildren();
  createRoot(rootEl).render(app);
}
